import { type FrameValue, useSpring } from '@react-spring/web'
import { useCallback, useState } from 'react'

import { type ValidationErrorMessages } from './types'

export interface StepPreviewAnimation {
  startBlinking: () => void
  artworkSizeOpacity: FrameValue<number> | number
  artworkImageOpacity: FrameValue<number> | number
  frameStyleOpacity: FrameValue<number> | number
  passepartoutOpacity: FrameValue<number> | number
  glassOpacity: FrameValue<number> | number
  spacerOpacity: FrameValue<number> | number
}

const timingPrecision = 1000
const blinkDuration = 400
const blinkCount = 3

export const defaultStepPreviewAnimation = {
  startBlinking: () => {},
  artworkSizeOpacity: 1,
  artworkImageOpacity: 1,
  frameStyleOpacity: 1,
  passepartoutOpacity: 1,
  glassOpacity: 1,
  spacerOpacity: 1,
}

/**
 * Returns opacity value and blinking state setter.
 */
export const useBlinkingState = () => {
  const [isBlinking, setIsBlinking] = useState(false)

  const { blinkingProgress } = useSpring({
    from: {
      blinkingProgress: 0,
    },
    blinkingProgress: isBlinking ? 1 : 0,
    config: {
      duration: blinkDuration * blinkCount,
    },
    reset: true,
    onRest: () => {
      setIsBlinking(false)
    },
  })

  // Calculate opacity value based on total time elapsed since start
  const opacity = blinkingProgress.to((value) =>
    Math.abs(
      (Math.round(value * blinkCount * 2 * timingPrecision) %
        (2 * timingPrecision)) /
        timingPrecision -
        1,
    ),
  )

  return [opacity, setIsBlinking] as const
}

/**
 * Manages step preview blinking and opacity values.
 */
export const useStepPreviewAnimation = (
  validationErrorMessages: ValidationErrorMessages,
): StepPreviewAnimation => {
  const [artworkSizeOpacity, setIsArtworkSizeBlinking] = useBlinkingState()
  const [frameStyleOpacity, setIsFrameStyleBlinking] = useBlinkingState()
  const [passepartoutOpacity, setIsPassepartoutBlinking] = useBlinkingState()
  const [glassOpacity, setIsGlassBlinking] = useBlinkingState()
  const [spacerOpacity, setIsSpacerBlinking] = useBlinkingState()

  const startBlinking = useCallback(() => {
    // Start blinking animation for invalid step previews
    if (validationErrorMessages?.artowkSizePreview) {
      setIsArtworkSizeBlinking(true)
    }

    if (
      validationErrorMessages?.frameStyleProfilePreview ||
      validationErrorMessages?.frameStyleTexturePreview
    ) {
      setIsFrameStyleBlinking(true)
    }

    if (
      validationErrorMessages?.passepartoutColorPreview ||
      validationErrorMessages?.passepartoutSizePreview
    ) {
      setIsPassepartoutBlinking(true)
    }

    if (validationErrorMessages?.glassTypePreview) {
      setIsGlassBlinking(true)
    }

    if (
      validationErrorMessages?.spacerSizePreview ||
      validationErrorMessages?.spacerColorPreview
    ) {
      setIsSpacerBlinking(true)
    }
  }, [
    setIsArtworkSizeBlinking,
    setIsFrameStyleBlinking,
    setIsGlassBlinking,
    setIsPassepartoutBlinking,
    setIsSpacerBlinking,
    validationErrorMessages,
  ])

  return {
    startBlinking,
    artworkSizeOpacity,
    artworkImageOpacity: 1,
    frameStyleOpacity,
    passepartoutOpacity,
    glassOpacity,
    spacerOpacity,
  }
}
